import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { Headphones, Mic, Play, Crown, Heart, Scroll, Flame, Layers, ArrowRight, Volume2, Clock, Sparkles, Bell, Music, Quote, Mountain, Lock, Radio, Users, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-3868">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-3869">
    <Eyebrow data-id="element-3870">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-3871">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-3872">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-3873" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-3874">
        {intro}
      </p>}
  </div>;
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-amber-400/40 pointer-events-none" data-id="element-3875" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-amber-400/40 pointer-events-none" data-id="element-3876" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-amber-400/40 pointer-events-none" data-id="element-3877" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-amber-400/40 pointer-events-none" data-id="element-3878" />
  </>;
// HERO
const Hero = () => <section className="relative w-full min-h-[100vh] bg-black flex items-center justify-center overflow-hidden pt-20" data-id="element-3879">
    <div className="absolute inset-0" data-id="element-3880">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.18),_transparent_55%)] pointer-events-none" data-id="element-3881" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(139,0,0,0.15),_transparent_60%)] pointer-events-none" data-id="element-3882" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-3883" />
    </div>

    <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 0.15
  }} transition={{
    duration: 2
  }} className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-3884">
      <div className="flex items-end gap-1 h-40" data-id="element-3885">
        {Array.from({
        length: 32
      }).map((_, i) => <motion.div key={i} animate={{
        scaleY: [0.3, 1, 0.5, 0.9, 0.3]
      }} transition={{
        duration: 3 + i % 5 * 0.4,
        repeat: Infinity,
        repeatType: 'mirror',
        delay: i * 0.05
      }} className="w-px bg-amber-400 origin-bottom" style={{
        height: `${20 + i % 6 * 12}px`
      }} data-id="element-3886" />)}
      </div>
    </motion.div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-3887">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 mb-8 bg-black/40 backdrop-blur-sm" data-id="element-3888">
        <Headphones className="w-3.5 h-3.5 text-amber-400" data-id="element-3889" />
        <span className="text-amber-400 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-3890">
          A Prestige Audio Franchise
        </span>
      </motion.div>

      <motion.h1 initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.15
    }} className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-[0.98] tracking-tight mb-4" data-id="element-3891">
        House of <span className="text-amber-400" data-id="element-3892">Lions</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 0.35
    }} className="font-serif italic text-lg sm:text-xl md:text-2xl text-white/70 mb-7 tracking-wide" data-id="element-3893">
        Voices of the Sikh Kingdoms
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      scaleX: 0
    }} animate={{
      opacity: 1,
      scaleX: 1
    }} transition={{
      duration: 0.9,
      delay: 0.45
    }} className="w-32 h-px bg-amber-400/50 mx-auto mb-9 origin-center" data-id="element-3894" />

      <motion.p initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.9,
      delay: 0.55
    }} className="text-base sm:text-lg md:text-xl text-white/65 font-light leading-relaxed max-w-3xl mx-auto mb-12" data-id="element-3895">
        A living world of power, love, discipline, fracture, pluralism,
        inheritance, and loss — experienced not as a lesson from the past, but
        as a court still breathing.
      </motion.p>

      <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3896">
        <a href="#episode-one" className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.35)]" data-id="element-3897">
          <Play className="w-4 h-4 fill-black" data-id="element-3898" />
          Listen to Episode One
        </a>
        <a href="#season-one" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-3899">
          Explore Season One
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-3900" />
        </a>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 1
    }} className="mt-14 flex items-center justify-center gap-6 text-[10px] tracking-[0.3em] uppercase text-white/35" data-id="element-3901">
        <span data-id="element-3902">8 Episodes</span>
        <span className="text-amber-400/30" data-id="element-3903">·</span>
        <span data-id="element-3904">Season One</span>
        <span className="text-amber-400/30" data-id="element-3905">·</span>
        <span data-id="element-3906">Now Recording</span>
      </motion.div>
    </div>
  </section>;
// CENTRAL QUESTION
const CentralQuestion = () => <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3907">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.04),_transparent_70%)] pointer-events-none" data-id="element-3908" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-3909">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8
    }} className="max-w-4xl mx-auto text-center" data-id="element-3910">
        <Eyebrow data-id="element-3911">The Question That Bites</Eyebrow>
        <Quote className="w-8 h-8 text-amber-400/40 mx-auto mt-7 mb-7" strokeWidth={1.25} data-id="element-3912" />
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white leading-[1.15] tracking-tight mb-10" data-id="element-3913">
          What does it cost to build and hold a kingdom{' '}
          <span className="text-amber-400 italic" data-id="element-3914">worthy of memory</span> in a
          violent world?
        </h2>
        <div className="w-16 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-3915" />
        <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed italic max-w-2xl mx-auto" data-id="element-3916">
          And what kinds of people make an empire possible — what does history
          erase about them?
        </p>
      </motion.div>
    </div>
  </section>;
// THE WORLD
const TheWorld = () => {
  const pillars = [{
    icon: Crown,
    title: 'Court',
    body: 'Beauty, wit, etiquette, politics, ceremony, intimacy. The Darbar as a stage of human pressure.'
  }, {
    icon: Mountain,
    title: 'Frontier',
    body: 'Danger, endurance, cold steel, horses, dust, waiting, fear. Where empire is paid for in blood.'
  }, {
    icon: Flame,
    title: 'Faith & Discipline',
    body: 'Conviction, conscience, internal judgment, moral pressure. The unbending eye on power.'
  }, {
    icon: Layers,
    title: 'Pluralism & Statecraft',
    body: 'Diplomacy, coexistence, the burden of holding difference together without breaking it.'
  }, {
    icon: Scroll,
    title: 'Inheritance',
    body: 'Heirs, widows, memory, exile. What remains after the builders are gone.'
  }];
  return <section className="relative py-24 md:py-32 bg-black" data-id="element-3917">
      <div className="container mx-auto px-4" data-id="element-3918">
        <SectionHeading eyebrow="The World" title="Five Pressures That" highlight="Make the Kingdom" intro="Every episode lives inside the collision of these forces. The court is beautiful because it is difficult to hold." data-id="element-3919" />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-6xl mx-auto" data-id="element-3920">
          {pillars.map((p, i) => <motion.div key={p.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors bg-gradient-to-b from-amber-950/10 to-transparent p-5" data-id="element-3921">
              <span className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-3922" />
              <div className="w-9 h-9 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-4" data-id="element-3923">
                <p.icon className="w-4 h-4 text-amber-400" data-id="element-3924" />
              </div>
              <h3 className="font-serif text-lg text-white mb-2 leading-tight" data-id="element-3925">
                {p.title}
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3926">
                {p.body}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// SEASON ONE
const SeasonOne = () => {
  const episodes = [{
    n: '01',
    title: 'A Fractured Punjab',
    body: 'The need for order is introduced as dangerous, necessary, and incomplete.',
    featured: true
  }, {
    n: '02',
    title: 'The Boy Who Survived',
    body: 'Ranjit Singh enters not as legend but as a marked young force of perception and will.'
  }, {
    n: '03',
    title: 'Lahore Taken',
    body: 'The city becomes the emotional and political heart of the story.'
  }, {
    n: '04',
    title: 'A Court of Many Worlds',
    body: 'Beauty, diplomacy, intimacy, and contradiction define the Darbar.'
  }, {
    n: '05',
    title: 'The Frontier Demands Blood',
    body: 'Nalwa and the edge of empire force the audience to confront cost.'
  }, {
    n: '06',
    title: 'Love, Reputation, and the Human Court',
    body: 'Moran shifts the center from public legend to interior life.'
  }, {
    n: '07',
    title: 'The Weight of Holding It Together',
    body: 'Moral and political tensions tighten around the builders.'
  }, {
    n: '08',
    title: 'What Remains, What Breaks',
    body: 'The season lands with beauty, power, and unresolved fragility.'
  }];
  return <section id="season-one" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-3927">
      <div className="container mx-auto px-4" data-id="element-3928">
        <SectionHeading eyebrow="Season One" title="The Making of" highlight="Lahore" intro="A fractured land does not become a kingdom by force alone. It becomes one through scarred vision, dangerous loyalty, disciplined belief, intimate contradiction, and people willing to carry more than history remembers." data-id="element-3929" />
        <div className="mt-16 max-w-5xl mx-auto" data-id="element-3930">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="border-l-2 border-amber-400/50 pl-6 sm:pl-8 mb-12 max-w-3xl mx-auto" data-id="element-3931">
            <p className="font-serif text-lg sm:text-xl text-white/80 leading-relaxed italic" data-id="element-3932">
              &ldquo;The kingdom begins where fracture becomes
              intolerable.&rdquo;
            </p>
            <p className="text-amber-400/60 text-[10px] tracking-[0.3em] uppercase mt-3" data-id="element-3933">
              — Episode One, closing line
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-3934">
            {episodes.map((ep, i) => <motion.div key={ep.n} initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.04
          }} className={`relative border bg-gradient-to-b from-amber-950/8 to-transparent p-5 transition-colors ${ep.featured ? 'border-amber-400/50 hover:border-amber-400' : 'border-amber-400/15 hover:border-amber-400/35'}`} data-id="element-3935">
                <span className={`absolute -top-px left-5 right-5 h-px bg-gradient-to-r from-transparent ${ep.featured ? 'via-amber-400' : 'via-amber-400/40'} to-transparent`} data-id="element-3936" />
                <div className="flex items-baseline gap-4 mb-2" data-id="element-3937">
                  <span className="font-mono text-amber-400/60 text-[11px] tracking-[0.2em] shrink-0" data-id="element-3938">
                    EP {ep.n}
                  </span>
                  {ep.featured && <span className="text-[9px] tracking-[0.25em] uppercase text-amber-400 border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5" data-id="element-3939">
                      Featured
                    </span>}
                </div>
                <h4 className="font-serif text-lg text-white leading-tight mb-2" data-id="element-3940">
                  {ep.title}
                </h4>
                <p className="text-white/55 text-sm font-light leading-relaxed" data-id="element-3941">
                  {ep.body}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
};
// ENSEMBLE
const Ensemble = () => {
  const cast = [{
    name: 'Maharaja Ranjit Singh',
    role: 'The Gravitational Center',
    desire: 'To build something larger than faction.',
    wound: 'A body marked early by illness and perception.',
    contradiction: 'Tenderness and ruthlessness.',
    motif: 'Restrained strings, low pulse — scarred authority, never triumph.'
  }, {
    name: 'Hari Singh Nalwa',
    role: 'Frontier Blade',
    desire: 'To secure the edge.',
    wound: 'A life defined by danger and action.',
    contradiction: 'Heroic necessity versus moral cost.',
    motif: 'Hard percussion, horse rhythm — long pauses that suggest the cost of force.'
  }, {
    name: 'Moran Sarkar',
    role: 'The Human Interior',
    desire: 'To be seen beyond reduction.',
    wound: 'The politics of intimacy and reputation.',
    contradiction: 'Softness in a hard court, wit in a punitive world.',
    motif: 'Lyrical fragments, intimate room tone — wit and ache, not softness alone.'
  }, {
    name: 'Fakir Azizuddin',
    role: 'Pluralism & Statecraft',
    desire: 'To hold the court together through intelligence and diplomacy.',
    wound: 'Speaking across difference in a world of suspicion.',
    contradiction: 'Gentle manner, razor political mind.',
    motif: 'Elegant tonal clarity, diplomatic steadiness, linguistic precision.'
  }, {
    name: 'Akali Phula Singh',
    role: 'Moral Force',
    desire: 'To keep moral force above political vanity.',
    wound: 'Unbending conscience in an imperfect world.',
    contradiction: 'Spiritual legitimacy and disruptive ferocity.',
    motif: 'Austerity, sharpened breath, conviction carried through stillness.'
  }, {
    name: 'Maharani Jind Kaur',
    role: 'Inheritance Under Pressure',
    desire: 'To preserve inheritance and agency.',
    wound: 'Entering power through fracture, not stability.',
    contradiction: 'Maternal tenderness and political steel.',
    motif: 'Tensioned regal motif — inheritance bending but not breaking.'
  }, {
    name: 'Duleep Singh',
    role: 'The Shadow of What Can Be Lost',
    desire: 'To belong to a story already breaking before he can hold it.',
    wound: 'Inheritance through removal and exile.',
    contradiction: 'Symbolic value versus personal dispossession.',
    motif: 'Motifs of distance and displacement — a kingdom no longer held.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-3942">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-3943" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3944">
        <SectionHeading eyebrow="The Ensemble" title="Seven People Who" highlight="Carry the Court" intro="Each lives at the collision of a desire, a wound, and a contradiction. Each has a sonic identity you will recognize within seconds of an episode beginning." data-id="element-3945" />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-3946">
          {cast.map((c, i) => <motion.div key={c.name} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 transition-colors bg-gradient-to-b from-amber-950/10 to-black p-6" data-id="element-3947">
              <CornerOrnaments data-id="element-3948" />
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/55 to-transparent" data-id="element-3949" />
              <p className="text-amber-400 text-[9px] tracking-[0.3em] uppercase font-bold mb-3" data-id="element-3950">
                {c.role}
              </p>
              <h3 className="font-serif text-xl text-white leading-tight mb-5" data-id="element-3951">
                {c.name}
              </h3>
              <dl className="space-y-3 text-sm" data-id="element-3952">
                <div data-id="element-3953">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-3954">
                    Desire
                  </dt>
                  <dd className="text-white/70 font-light leading-relaxed" data-id="element-3955">
                    {c.desire}
                  </dd>
                </div>
                <div data-id="element-3956">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-3957">
                    Wound
                  </dt>
                  <dd className="text-white/70 font-light leading-relaxed" data-id="element-3958">
                    {c.wound}
                  </dd>
                </div>
                <div data-id="element-3959">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1" data-id="element-3960">
                    Contradiction
                  </dt>
                  <dd className="text-white/70 font-light leading-relaxed" data-id="element-3961">
                    {c.contradiction}
                  </dd>
                </div>
                <div className="pt-3 border-t border-amber-400/10" data-id="element-3962">
                  <dt className="text-amber-400/55 text-[9px] tracking-[0.3em] uppercase mb-1 flex items-center gap-1.5" data-id="element-3963">
                    <Music className="w-2.5 h-2.5" data-id="element-3964" />
                    Sonic Motif
                  </dt>
                  <dd className="text-white/55 font-light leading-relaxed italic text-[13px]" data-id="element-3965">
                    {c.motif}
                  </dd>
                </div>
              </dl>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// EPISODE ONE
const EpisodeOne = () => {
  const [playing, setPlaying] = useState(false);
  return <section id="episode-one" className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-3966">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_65%)] pointer-events-none" data-id="element-3967" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-3968">
        <div className="max-w-5xl mx-auto" data-id="element-3969">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-3970">
            <Eyebrow data-id="element-3971">Episode One</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-4 leading-tight" data-id="element-3972">
              A <span className="text-amber-400" data-id="element-3973">Fractured</span> Punjab
            </h2>
            <div className="flex items-center justify-center gap-4 text-[10px] tracking-[0.3em] uppercase text-white/45 mt-6" data-id="element-3974">
              <span className="flex items-center gap-1.5" data-id="element-3975">
                <Clock className="w-3 h-3" data-id="element-3976" />
                32 min
              </span>
              <span className="text-amber-400/30" data-id="element-3977">·</span>
              <span data-id="element-3978">Season One Premiere</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-3979">
            <motion.div initial={{
            opacity: 0,
            x: -16
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:col-span-3 relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 via-black to-black p-8 sm:p-10" data-id="element-3980">
              <CornerOrnaments data-id="element-3981" />
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-3982" />
              <div className="flex items-center gap-5 mb-8" data-id="element-3983">
                <button onClick={() => setPlaying(!playing)} className="group relative w-20 h-20 sm:w-24 sm:h-24 border-2 border-amber-400 bg-amber-400/10 hover:bg-amber-400/20 transition-colors flex items-center justify-center shrink-0 shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-3984">
                  {playing ? <div className="flex gap-1" data-id="element-3985">
                      <span className="w-1.5 h-7 bg-amber-400" data-id="element-3986" />
                      <span className="w-1.5 h-7 bg-amber-400" data-id="element-3987" />
                    </div> : <Play className="w-8 h-8 text-amber-400 fill-amber-400 ml-1" data-id="element-3988" />}
                </button>
                <div className="flex-1 min-w-0" data-id="element-3989">
                  <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase mb-1.5 font-bold" data-id="element-3990">
                    {playing ? 'Now Playing' : 'Listen Now'}
                  </p>
                  <p className="font-serif text-xl sm:text-2xl text-white leading-tight" data-id="element-3991">
                    A Fractured Punjab
                  </p>
                  <p className="text-white/40 text-xs mt-1 font-light" data-id="element-3992">
                    Episode 1 · House of Lions Season One
                  </p>
                </div>
              </div>

              <div className="mb-6" data-id="element-3993">
                <div className="relative h-12 flex items-center gap-0.5" data-id="element-3994">
                  {Array.from({
                  length: 60
                }).map((_, i) => <motion.span key={i} animate={playing ? {
                  scaleY: [0.4, 1.2, 0.6, 0.9, 0.4]
                } : {
                  scaleY: 0.4
                }} transition={{
                  duration: 1.5 + i % 4 * 0.2,
                  repeat: Infinity,
                  repeatType: 'mirror',
                  delay: i * 0.02
                }} className={`flex-1 origin-center transition-colors ${i < 12 ? 'bg-amber-400' : 'bg-amber-400/20'}`} style={{
                  height: `${20 + i % 5 * 6}px`
                }} data-id="element-3995" />)}
                </div>
                <div className="flex items-center justify-between text-[10px] font-mono text-white/40 mt-2" data-id="element-3996">
                  <span className="text-amber-400" data-id="element-3997">06:12</span>
                  <span data-id="element-3998">32:00</span>
                </div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-3999">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-white/50" data-id="element-4000">
                  <Volume2 className="w-3 h-3 text-amber-400" data-id="element-4001" />
                  Best with headphones
                </div>
                <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-amber-400/70" data-id="element-4002">
                  <Bell className="w-3 h-3" data-id="element-4003" />
                  Subscribe to follow
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 16
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:col-span-2 relative border border-amber-400/15 bg-gradient-to-b from-amber-950/10 to-transparent p-7" data-id="element-4004">
              <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" data-id="element-4005" />
              <Quote className="w-5 h-5 text-amber-400/40 mb-4" strokeWidth={1.25} data-id="element-4006" />
              <p className="text-amber-400 text-[9px] tracking-[0.3em] uppercase font-bold mb-4" data-id="element-4007">
                Cold Open
              </p>
              <p className="font-serif text-lg text-white/80 leading-relaxed mb-5" data-id="element-4008">
                &ldquo;Punjab did not begin as one song. It began as many fires,
                burning close enough to warm the same dark, far enough apart to
                mistake each other for threat.&rdquo;
              </p>
              <p className="text-white/45 text-sm font-light italic leading-relaxed" data-id="element-4009">
                In a fractured land, history is not first felt in crowns. It is
                felt in doors bolted before dusk.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6" data-id="element-4010">
            {[{
            n: '01',
            title: 'Chorus of Fracture',
            body: 'A montage of voices — soldier, merchant, elder, rider — that refuses to resolve.'
          }, {
            n: '04',
            title: 'The Logic of the Misls',
            body: '"Boys always think a sword is an answer. Men live long enough to learn it is a receipt."'
          }, {
            n: '08',
            title: 'Lahore as Desire',
            body: 'Cities do not love ambition. They ask: are you here to possess me, or to be remade?'
          }].map(s => <div key={s.n} className="border border-amber-400/15 bg-black/40 p-4" data-id="element-4011">
                <p className="font-mono text-amber-400/55 text-[10px] tracking-[0.2em] mb-1.5" data-id="element-4012">
                  SCENE {s.n}
                </p>
                <h4 className="font-serif text-white text-base leading-tight mb-2" data-id="element-4013">
                  {s.title}
                </h4>
                <p className="text-white/50 text-xs font-light leading-relaxed italic" data-id="element-4014">
                  {s.body}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
// SONIC IDENTITY
const SonicIdentity = () => {
  const zones = [{
    icon: Crown,
    name: 'Lahore Court',
    desc: 'Layered air, distant cloth, softened marble reverb, subtle metal ornament, birds and fountain at the edge.'
  }, {
    icon: Mountain,
    name: 'The Frontier',
    desc: 'Dry wind, saddle leather, hoof rhythm, iron, wider silence, colder space, distant threat.'
  }, {
    icon: Heart,
    name: 'Private Chamber',
    desc: 'Lower room tone, breath, jewelry contact, close fabric, intimacy with danger.'
  }, {
    icon: Flame,
    name: 'Faith & Judgment',
    desc: 'Reduced instrumentation, room pressure, tonal narrowing, voice against stillness.'
  }, {
    icon: Scroll,
    name: 'Memory & Inheritance',
    desc: 'Degraded textures, echo fragments, recurring motifs in altered form.'
  }];
  return <section className="relative py-24 md:py-32 bg-[#080808] border-t border-amber-400/10" data-id="element-4015">
      <div className="container mx-auto px-4" data-id="element-4016">
        <SectionHeading eyebrow="The Sonic World" title="Sound as" highlight="World-Building Language" intro="Each place, person, and moral force carries a recognizable sonic signature. The world is built not through narration, but through breath, room tone, and rhythm." data-id="element-4017" />
        <div className="mt-14 max-w-5xl mx-auto space-y-3" data-id="element-4018">
          {zones.map((z, i) => <motion.div key={z.name} initial={{
          opacity: 0,
          x: -16
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="relative flex items-start gap-5 border border-amber-400/15 hover:border-amber-400/35 transition-colors bg-gradient-to-r from-amber-950/10 to-transparent p-6" data-id="element-4019">
              <div className="w-11 h-11 border border-amber-400/40 bg-black/40 flex items-center justify-center shrink-0" data-id="element-4020">
                <z.icon className="w-5 h-5 text-amber-400" strokeWidth={1.25} data-id="element-4021" />
              </div>
              <div className="flex-1" data-id="element-4022">
                <h4 className="font-serif text-xl text-white leading-tight mb-1.5" data-id="element-4023">
                  {z.name}
                </h4>
                <p className="text-white/55 text-sm sm:text-[15px] font-light leading-relaxed" data-id="element-4024">
                  {z.desc}
                </p>
              </div>
              <div className="hidden sm:flex items-end gap-0.5 h-8 shrink-0" data-id="element-4025">
                {Array.from({
              length: 6
            }).map((_, j) => <span key={j} className="w-0.5 bg-amber-400/40" style={{
              height: `${10 + (i * 3 + j) % 5 * 4}px`
            }} data-id="element-4026" />)}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// LISTENING ARCHITECTURE
const ListeningArchitecture = () => {
  const formats = [{
    icon: Headphones,
    tag: 'Flagship',
    title: 'House of Lions',
    sub: 'Season One: The Making of Lahore',
    desc: 'Eight prestige dramatic episodes. The narrative core of the franchise.',
    access: 'Public · All platforms'
  }, {
    icon: Mic,
    tag: 'Short-Form',
    title: 'Voices of the Court',
    sub: 'Portrait Monologues',
    desc: 'Single-character intimate monologues — direct voice, no narration.',
    access: 'Public · Discovery'
  }, {
    icon: Radio,
    tag: 'Ambient',
    title: 'Echoes of Lahore',
    sub: 'Place & Memory Worlds',
    desc: 'Ambient sound worlds for slow listening. Court, frontier, chamber, courtyard.',
    access: 'Public · Atmospheric'
  }, {
    icon: Lock,
    tag: 'Premium',
    title: 'Sealed Chambers',
    sub: 'Treasury & Patron Drops',
    desc: 'Extended scenes, alternate perspectives, unreleased monologues. Unlocked by XP, Misls, and Court status.',
    access: 'Premium · Treasury-linked',
    featured: true
  }, {
    icon: Users,
    tag: 'Live',
    title: 'Darbar Sessions',
    sub: 'Salons & Event Audio',
    desc: 'Live recordings from in-person events. City-specific drops, conversations with the creators.',
    access: 'Event & live spillover'
  }];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10" data-id="element-4027">
      <div className="container mx-auto px-4" data-id="element-4028">
        <SectionHeading eyebrow="The Listening Architecture" title="Five Ways to" highlight="Enter the World" intro="A prestige narrative core surrounded by social-first character clips, ambient worlds, premium collectible-linked drops, and live event spillover." data-id="element-4029" />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-id="element-4030">
          {formats.map((f, i) => <motion.div key={f.title} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.06
        }} className={`relative border bg-gradient-to-b from-amber-950/10 to-black p-6 transition-colors ${f.featured ? 'border-amber-400/45 hover:border-amber-400' : 'border-amber-400/15 hover:border-amber-400/40'}`} data-id="element-4031">
              <span className={`absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent ${f.featured ? 'via-amber-400' : 'via-amber-400/55'} to-transparent`} data-id="element-4032" />
              <div className="flex items-center justify-between mb-4" data-id="element-4033">
                <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center" data-id="element-4034">
                  <f.icon className="w-4 h-4 text-amber-400" data-id="element-4035" />
                </div>
                <span className={`text-[9px] tracking-[0.3em] uppercase font-bold px-2 py-1 border ${f.featured ? 'border-amber-400 text-amber-400 bg-amber-400/10' : 'border-amber-400/30 text-amber-400/80'}`} data-id="element-4036">
                  {f.tag}
                </span>
              </div>
              <h4 className="font-serif text-xl text-white leading-tight mb-1" data-id="element-4037">
                {f.title}
              </h4>
              <p className="text-amber-400/70 text-[11px] tracking-[0.2em] uppercase mb-3 font-light" data-id="element-4038">
                {f.sub}
              </p>
              <p className="text-white/55 text-sm font-light leading-relaxed mb-4" data-id="element-4039">
                {f.desc}
              </p>
              <p className="text-white/35 text-[10px] tracking-[0.25em] uppercase border-t border-amber-400/10 pt-3 flex items-center gap-1.5" data-id="element-4040">
                <span className="text-amber-400/60" data-id="element-4041">•</span> {f.access}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// CLOSING CTA
const ClosingCTA = () => {
  const platforms = ['Apple Podcasts', 'Spotify', 'YouTube', 'Overcast', 'Pocket Casts'];
  return <section className="relative py-24 md:py-32 bg-black border-t border-amber-400/10 overflow-hidden" data-id="element-4042">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.1),_transparent_60%)] pointer-events-none" data-id="element-4043" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(139,0,0,0.08),_transparent_60%)] pointer-events-none" data-id="element-4044" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-4045">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mx-auto text-center" data-id="element-4046">
          <Eyebrow data-id="element-4047">Begin Listening</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-4048">
            The court is{' '}
            <span className="text-amber-400 italic" data-id="element-4049">still breathing.</span>
          </h2>
          <div className="w-20 h-px bg-amber-400/40 mx-auto mb-8" data-id="element-4050" />
          <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed mb-10" data-id="element-4051">
            Subscribe to House of Lions on your platform of choice. Or step
            deeper into the world through Lahore Treasury, where every episode
            unlocks new chambers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10" data-id="element-4052">
            <a href="#episode-one" className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-amber-400 text-black text-xs tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]" data-id="element-4053">
              <Play className="w-4 h-4 fill-black" data-id="element-4054" />
              Listen to Episode One
            </a>
            <a href="/treasure" className="group inline-flex items-center justify-center gap-2 px-6 py-4 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-4055">
              <Sparkles className="w-3.5 h-3.5" data-id="element-4056" />
              Enter Lahore Treasury
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-4057" />
            </a>
          </div>
          <div className="border-t border-amber-400/10 pt-8" data-id="element-4058">
            <p className="text-amber-400/55 text-[10px] tracking-[0.3em] uppercase mb-5 font-light" data-id="element-4059">
              Available On
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-4060">
              {platforms.map(p => <span key={p} className="px-3 py-1.5 border border-amber-400/20 text-white/55 text-[11px] tracking-wide hover:border-amber-400/50 hover:text-white/80 transition-colors cursor-pointer" data-id="element-4061">
                  {p}
                </span>)}
            </div>
            <p className="mt-6 text-white/35 text-xs font-light italic flex items-center justify-center gap-2" data-id="element-4062">
              <ChevronRight className="w-3 h-3 text-amber-400/50" data-id="element-4063" />
              Episodes release weekly when the season opens.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
};
export const HouseOfLionsPage = () => <LanguageProvider data-id="element-4064">
    <div className="bg-black min-h-screen text-white selection:bg-amber-400/30" data-id="element-4065">
      <ScrollProgress data-id="element-4066" />
      <Navbar data-id="element-4067" />
      <main data-id="element-4068">
        <Hero data-id="element-4069" />
        <CentralQuestion data-id="element-4070" />
        <TheWorld data-id="element-4071" />
        <SeasonOne data-id="element-4072" />
        <Ensemble data-id="element-4073" />
        <EpisodeOne data-id="element-4074" />
        <SonicIdentity data-id="element-4075" />
        <ListeningArchitecture data-id="element-4076" />
        <ClosingCTA data-id="element-4077" />
      </main>
      <Footer data-id="element-4078" />
    </div>
  </LanguageProvider>;